import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Sparkles,
  Server,
  Braces,
  Smartphone,
  ShieldCheck,
  GitBranch,
  ChevronRight,
  Check,
  CalendarClock,
  Clock,
  MessageCircle,
  Quote,
  Star,
  Zap,
  Layers,
  Target,
  Brain,
  Briefcase,
  Award,
} from "lucide-react";
import { useI18n } from "@/lib/i18nContext";
import { api } from "@/lib/api";
import { resolveMediaUrl } from "@/lib/media";
import TechBubbles from "@/components/TechBubbles";
import Seo, { personSchema, websiteSchema } from "@/components/Seo";

const ICON_MAP = { Server, Braces, Smartphone, ShieldCheck, GitBranch, Sparkles };

const CLIENT_LOGOS = ["AUDI", "BMW", "iC CONSULT", "IN-TECH", "BERTRANDT", "CATBIRD SEAT", "AUTOSCOUT24"];

const VALUE_ICONS = [Target, Zap, Layers, ShieldCheck, Brain];
const PROCESS_STEPS = ["01", "02", "03", "04"];

const LOCALE_FOR_LANG = { de: "de-DE", en: "en-GB", fr: "fr-FR" };

export default function HomePage() {
  const { t, tl, lang, dict, pathWithLang } = useI18n();
  const [services, setServices] = useState([]);
  const [posts, setPosts] = useState([]);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data || [])).catch(() => {});
    api.get("/blog?limit=3").then((r) => setPosts(r.data || [])).catch(() => {});
    api.get("/profile").then((r) => setProfile(r.data)).catch(() => {});
  }, []);

  const VALUES = (dict?.why?.values || []).map((v, i) => ({ ...v, icon: VALUE_ICONS[i] }));
  const PROCESS = (dict?.process?.steps || []).map((p, i) => ({ ...p, step: PROCESS_STEPS[i] }));
  const dateLocale = LOCALE_FOR_LANG[lang] || "de-DE";

  // Safe inline renderer for our small i18n markup. We support <gold>...</gold> and <strong>...</strong>
  // ONLY — anything else (including any user-controlled content) is rendered as plain text.
  // This replaces the previous dangerouslySetInnerHTML usage and eliminates XSS risk.
  const renderInlineMarkup = (input) => {
    if (!input) return null;
    const pattern = /<(gold|strong)>([\s\S]*?)<\/\1>/g;
    const out = [];
    let lastIndex = 0;
    let m;
    let key = 0;
    while ((m = pattern.exec(input)) !== null) {
      if (m.index > lastIndex) out.push(input.slice(lastIndex, m.index));
      const tag = m[1];
      const inner = m[2];
      if (tag === "gold") {
        out.push(
          <span key={`g${key++}`} className="gold-grad">{inner}</span>
        );
      } else {
        out.push(<strong key={`s${key++}`} className="text-[var(--text-0)]">{inner}</strong>);
      }
      lastIndex = m.index + m[0].length;
    }
    if (lastIndex < input.length) out.push(input.slice(lastIndex));
    return out;
  };

  return (
    <div data-testid="home-page">
      <Seo
        title={t("seo.home.title")}
        description={t("seo.home.description")}
        type="website"
        jsonLd={[personSchema, websiteSchema]}
      />
      {/* HERO */}
      <section className="relative overflow-hidden noise">
        <div className="absolute inset-0 -z-0 pointer-events-none">
          <div className="absolute inset-0 bg-hero-ambient" style={{ backgroundImage: "url(/visuals/hero-ambient.png)" }} />
          <div className="absolute top-[-30%] right-[-15%] w-[60vw] h-[60vw] rounded-full bg-[var(--gold)] opacity-[0.06] blur-[140px]" />
          <div className="absolute bottom-[-10%] left-[-10%] w-[40vw] h-[40vw] rounded-full bg-[var(--gold-soft)] opacity-[0.5] blur-[100px]" />
        </div>

        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 pt-16 lg:pt-24 pb-20 lg:pb-28 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* LEFT: Text + CTA */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 pill-gold mb-8 reveal-up" style={{ animationDelay: "0.05s" }}>
                <span className="w-2 h-2 rounded-full bg-[var(--gold)] pulse-gold" />
                <span className="text-xs font-medium">{t("hero.pillAvailable")}</span>
              </div>

              <h1
                data-testid="hero-title"
                className="font-display text-[clamp(2.5rem,6.5vw,5.25rem)] leading-[1.02] tracking-[-0.035em] reveal-up"
                style={{ animationDelay: "0.15s" }}
              >
                {t("hero.titleA")}{" "}
                <span className="gold-grad">{t("hero.titleAccent")}</span>{" "}
                {t("hero.titleB")}{" "}{t("hero.titleC")}
              </h1>

              <p
                className="mt-7 text-lg lg:text-xl text-[var(--text-1)] max-w-2xl leading-relaxed reveal-up"
                style={{ animationDelay: "0.25s" }}
              >
                {renderInlineMarkup(t("hero.intro"))}{" "}
                <span className="font-serif text-[var(--text-0)]">{t("hero.tagline")}</span>
              </p>

              <div className="mt-10 flex flex-wrap gap-3 reveal-up" style={{ animationDelay: "0.35s" }}>
                <Link to={pathWithLang("/kontakt")} data-testid="hero-book-btn" className="btn-primary">
                  <CalendarClock size={16} />
                  {t("common.bookFree")}
                </Link>
                <Link to={pathWithLang("/dienste")} data-testid="hero-services-btn" className="btn-ghost">
                  {t("common.seeServices")}
                  <ArrowUpRight size={14} />
                </Link>
              </div>

              {/* Quick stats */}
              <div className="mt-14 grid grid-cols-3 gap-4 lg:gap-8 max-w-xl reveal-up" style={{ animationDelay: "0.45s" }}>
                <div>
                  <div className="num font-display text-3xl lg:text-4xl gold-grad">9+</div>
                  <div className="text-xs text-[var(--text-2)] mt-1 uppercase tracking-wider">{t("hero.stats.yearsExp")}</div>
                </div>
                <div>
                  <div className="num font-display text-3xl lg:text-4xl gold-grad">13</div>
                  <div className="text-xs text-[var(--text-2)] mt-1 uppercase tracking-wider">{t("hero.stats.stations")}</div>
                </div>
                <div>
                  <div className="num font-display text-3xl lg:text-4xl gold-grad">2</div>
                  <div className="text-xs text-[var(--text-2)] mt-1 uppercase tracking-wider">{t("hero.stats.certs")}</div>
                </div>
              </div>
            </div>

            {/* RIGHT: Portrait + Card */}
            <div className="lg:col-span-5 relative reveal-up" style={{ animationDelay: "0.5s" }}>
              <div className="relative">
                <div className="portrait-frame aspect-[4/5] max-w-md mx-auto lg:ml-auto">
                  <img
                    src="/karim-portrait.webp"
                    alt="Karim Benna"
                    className="w-full h-full object-contain object-bottom"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>

                {/* Floating availability card */}
                <div className="absolute -bottom-6 -left-6 lg:-left-12 bg-[var(--bg-1)] border hairline-gold p-5 rounded shadow-2xl max-w-[260px] backdrop-blur-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2 h-2 rounded-full bg-[var(--success)]" />
                    <span className="text-xs font-mono text-[var(--text-2)] uppercase tracking-wider">
                      {t("common.responseTime")}
                    </span>
                  </div>
                  <div className="text-[var(--text-0)] font-medium">
                    {t("hero.responseShort")}
                  </div>
                  <div className="text-xs text-[var(--text-2)] mt-1">
                    {t("hero.langChips")}
                  </div>
                </div>

                {/* Top-right rating */}
                <div className="absolute -top-4 -right-4 lg:-right-4 bg-[var(--gold)] text-black px-4 py-3 rounded shadow-2xl hidden sm:block">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} size={12} fill="currentColor" strokeWidth={0} />
                    ))}
                  </div>
                  <div className="text-xs font-semibold mt-1">Senior Engineer</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Client strip */}
        <div className="border-y hairline bg-[var(--bg-1)]/50 backdrop-blur-sm relative z-10">
          <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-6 flex flex-col md:flex-row items-center gap-6">
            <div className="text-xs uppercase tracking-[0.2em] text-[var(--text-2)] font-mono whitespace-nowrap shrink-0">
              {t("hero.clientStrip")}
            </div>
            <div className="overflow-hidden flex-1 w-full">
              <div className="flex marquee-track gap-12 lg:gap-16 items-center whitespace-nowrap">
                {[...Array(2)].map((_, k) => (
                  <div key={k} className="flex gap-12 lg:gap-16 items-center shrink-0">
                    {CLIENT_LOGOS.map((c) => (
                      <span
                        key={c + k}
                        className="font-display font-bold text-xl lg:text-2xl text-[var(--text-2)] hover:text-[var(--gold)] transition-colors tracking-wider"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* QUICK ACCESS — direct entry points for visitors */}
      <section data-testid="quick-access" className="relative border-b hairline overflow-hidden">
        {/* Layered ambient visuals (bokeh image + tech-logo bubbles + subtle vignette) */}
        <div aria-hidden="true" className="absolute inset-0 bg-[var(--bg-0)]" />
        <div aria-hidden="true" className="absolute inset-0 bg-bokeh" style={{ backgroundImage: "url(/visuals/quick-access-bg.png)" }} />
        <TechBubbles />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(60% 80% at 50% 50%, transparent 0%, rgba(255,255,255,0.55) 60%, rgba(255,255,255,0.85) 100%)",
          }}
        />

        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16 lg:py-20 relative z-10">
          <div className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-[var(--gold)] font-medium mb-4">
                · {t("quick.label")}
              </div>
              <h2 className="font-display text-3xl lg:text-4xl leading-[1.05] tracking-tight">
                {t("quick.title")} <span className="font-serif gold-grad">{t("quick.titleAccent")}</span>{t("quick.titleEnd")}
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { key: "tools",      icon: Layers,    to: pathWithLang("/dienste") + "#skills",            bg: "/visuals/cards/card-tools.png" },
              { key: "certs",      icon: Award,     to: pathWithLang("/erfahrung") + "#certifications", bg: "/visuals/cards/card-certs.png" },
              { key: "experience", icon: Briefcase, to: pathWithLang("/erfahrung"),                      bg: "/visuals/cards/card-experience.png" },
              { key: "services",   icon: Target,    to: pathWithLang("/dienste"),                        bg: "/visuals/cards/card-services.png" },
            ].map(({ key, icon: Icon, to, bg }) => (
              <Link
                key={key}
                to={to}
                data-testid={`quick-${key}`}
                className="quick-card group relative flex flex-col items-start gap-5 overflow-hidden rounded-md border hairline-gold transition-all duration-300 hover:border-[var(--gold)]/60"
              >
                {/* Themed word-cloud backdrop */}
                <div
                  aria-hidden="true"
                  className="absolute inset-0 transition-transform duration-700 ease-out group-hover:scale-105"
                  style={{
                    backgroundImage: `url(${bg})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    opacity: 0.55,
                  }}
                />
                {/* Dark gradient overlay for readability */}
                <div
                  aria-hidden="true"
                  className="absolute inset-0 transition-opacity duration-300 group-hover:opacity-70"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.85) 55%, rgba(255,255,255,0.94) 100%)",
                  }}
                />

                {/* Content */}
                <div className="relative z-10 p-6 flex flex-col gap-5 h-full w-full">
                  <div className="w-12 h-12 rounded bg-[var(--gold)]/10 border hairline-gold flex items-center justify-center transition-colors duration-200 group-hover:bg-[var(--gold)]/25 backdrop-blur-sm">
                    <Icon size={20} className="text-[var(--gold)]" />
                  </div>
                  <div className="flex-1">
                    <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--text-2)] mb-2">
                      {t(`quick.${key}.label`)}
                    </div>
                    <h3 className="font-display text-lg lg:text-xl text-[var(--text-0)] leading-snug">
                      {t(`quick.${key}.title`)}
                    </h3>
                  </div>
                  <ArrowUpRight
                    size={18}
                    className="absolute top-6 right-6 text-[var(--text-2)] opacity-70 transition-all duration-200 group-hover:opacity-100 group-hover:text-[var(--gold)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* VALUE PROP / Why work with me */}
      <section className="relative overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0 bg-network pointer-events-none" style={{ backgroundImage: "url(/visuals/network-nodes.png)" }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{
          background: "linear-gradient(180deg, var(--bg-0) 0%, transparent 18%, transparent 82%, var(--bg-0) 100%)",
        }} />
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.22em] text-[var(--gold)] font-medium mb-4">
              · {t("why.label")}
            </div>
            <h2 className="font-display text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              {t("why.title")} <span className="font-serif gold-grad">{t("why.titleAccent")}</span>.
            </h2>
          </div>
          <p className="lg:col-span-7 lg:pt-12 text-[var(--text-1)] text-lg leading-relaxed max-w-xl lg:max-w-none">
            {t("why.desc")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
          {VALUES.map((v) => {
            const Icon = v.icon;
            return (
              <div key={v.title} className="svc-card themed-card backdrop-blur-md">
                <div
                  aria-hidden="true"
                  className="themed-card-backdrop"
                  style={{ backgroundImage: "url(/visuals/cards/values-metallic.png)" }}
                />
                <div aria-hidden="true" className="themed-card-overlay" />
                <div className="w-12 h-12 flex items-center justify-center bg-[var(--gold)]/10 border hairline-gold rounded mb-6 backdrop-blur-sm">
                  <Icon size={20} className="text-[var(--gold)]" />
                </div>
                <h3 className="font-display text-lg text-[var(--text-0)] mb-3">{v.title}</h3>
                <p className="text-[var(--text-1)] text-sm leading-relaxed">{v.desc}</p>
              </div>
            );
          })}
        </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="relative border-y hairline bg-[var(--bg-1)]/40 overflow-hidden">
        <div aria-hidden="true" className="absolute inset-0 bg-code-pattern pointer-events-none" style={{ backgroundImage: "url(/visuals/code-pattern.png)" }} />
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20 lg:py-28 relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-14 gap-6">
            <div className="max-w-2xl">
              <div className="text-xs uppercase tracking-[0.22em] text-[var(--gold)] font-medium mb-4">
                · {t("services.label")}
              </div>
              <h2 className="font-display text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                {t("services.title")} <span className="font-serif gold-grad">{t("services.titleAccent")}</span> {t("services.titleEnd")}
              </h2>
            </div>
            <Link to={pathWithLang("/dienste")} data-testid="services-cta" className="btn-ghost text-xs self-start lg:self-end">
              {t("services.seeAll")} <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((svc, idx) => {
              const Icon = ICON_MAP[svc.icon] || Sparkles;
              const isJsTs = svc.key === "js-ts-graphql";
              const isJavaSpring = svc.key === "java-spring";
              const isQaTest = svc.key === "qa-test";
              const isAiConsulting = svc.key === "ai-consulting";
              let cardTarget = pathWithLang("/dienste");
              let ctaLabel = t("common.learnMore");
              if (isJsTs) {
                cardTarget = pathWithLang("/blog/javascript-typescript-und-die-realitaet-komplexer-softwareprojekte");
                ctaLabel = t("services.jsTsCta");
              } else if (isJavaSpring) {
                cardTarget = pathWithLang("/blog/backend-entwicklung-spring-boot-und-die-realitaet-produktiver-cloud-systeme");
                ctaLabel = t("services.javaSpringCta");
              } else if (isQaTest) {
                cardTarget = pathWithLang("/blog/qualitaetssicherung-in-der-it-mehr-als-nur-testing");
                ctaLabel = t("services.qaTestCta");
              } else if (isAiConsulting) {
                cardTarget = pathWithLang("/blog/mit-applied-ai-data-science-program-abgeschlossen");
                ctaLabel = t("services.aiConsultingCta");
              }
              return (
                <Link
                  key={svc.key}
                  to={cardTarget}
                  data-testid={`service-card-${svc.key}`}
                  className="svc-card themed-card flex flex-col group"
                >
                  <div
                    aria-hidden="true"
                    className="themed-card-backdrop"
                    style={{ backgroundImage: `url(/visuals/cards/service-${svc.key}.png)` }}
                  />
                  <div aria-hidden="true" className="themed-card-overlay" />

                  <div className="flex items-start justify-between mb-6">
                    <div className="w-14 h-14 flex items-center justify-center bg-[var(--gold)]/10 border hairline-gold rounded backdrop-blur-sm">
                      <Icon size={22} className="text-[var(--gold)]" />
                    </div>
                    <span className="font-mono text-xs text-[var(--text-2)]">0{idx + 1}</span>
                  </div>
                  <div className="text-xs uppercase tracking-wider text-[var(--gold)] mb-2 font-medium">
                    {tl(svc.tagline)}
                  </div>
                  <h3 className="font-display text-xl text-[var(--text-0)] leading-tight mb-3 group-hover:text-[var(--gold)] transition-colors">
                    {tl(svc.title)}
                  </h3>
                  <p className="text-[var(--text-1)] text-sm leading-relaxed mb-5">
                    {tl(svc.description)}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mb-5">
                    {svc.stack?.slice(0, 3).map((s) => (
                      <span key={s} className="tag-pill">{s}</span>
                    ))}
                  </div>
                  <div className="mt-auto flex items-center gap-2 text-[var(--gold)] text-sm font-medium">
                    {ctaLabel}
                    <ArrowUpRight size={14} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.22em] text-[var(--gold)] font-medium mb-4">
              · {t("process.label")}
            </div>
            <h2 className="font-display text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              {t("process.titleA")} <span className="font-serif gold-grad">{t("process.titleAccent")}</span> {t("process.titleB")}
            </h2>
          </div>
          <p className="lg:col-span-7 lg:pt-12 text-[var(--text-1)] text-lg leading-relaxed max-w-xl lg:max-w-none">
            {t("process.desc")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-0">
          {PROCESS.map((p, i) => (
            <div
              key={p.step}
              className={`relative pt-8 ${i < 3 ? "lg:pr-8 lg:border-r hairline" : ""} ${i > 0 ? "lg:pl-8" : ""}`}
            >
              <div className="step-num mb-4">{p.step}</div>
              <h3 className="font-display text-xl text-[var(--text-0)] mb-3">{p.title}</h3>
              <p className="text-[var(--text-1)] text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PERSONAL TEASER */}
      <section className="border-y hairline bg-[var(--bg-1)]/30">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="relative max-w-sm mx-auto lg:mx-0">
              <div className="portrait-frame aspect-[4/5]">
                <img
                  src="/karim-portrait.webp"
                  alt="Karim Benna"
                  className="w-full h-full object-contain object-bottom"
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.22em] text-[var(--gold)] font-medium mb-4">
              · {t("teaser.label")}
            </div>
            <h2 className="font-display text-4xl lg:text-5xl tracking-tight leading-[1.08] mb-7">
              {t("teaser.titleA")}{" "}
              <span className="font-serif gold-grad">{t("teaser.titleAccent")}</span>{t("teaser.titleEnd")}
            </h2>
            <p className="text-[var(--text-1)] text-lg leading-relaxed mb-6">
              {renderInlineMarkup(t("teaser.intro"))}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8 max-w-xl">
              {["Java/Spring", "TypeScript", "GraphQL", "AWS", "Microservices", "ISTQB"].map((s) => (
                <div key={s} className="flex items-center gap-2 text-sm text-[var(--text-1)]">
                  <Check size={14} className="text-[var(--gold)] shrink-0" />
                  <span>{s}</span>
                </div>
              ))}
            </div>
            <Link to={pathWithLang("/ueber-mich")} data-testid="about-cta" className="btn-ghost text-xs">
              {t("common.moreAbout")} <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIAL/MOTTO */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
        <div className="max-w-4xl mx-auto text-center">
          <Quote size={36} className="text-[var(--gold)] mx-auto mb-8" />
          <blockquote className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.2] text-[var(--text-0)]">
            „{renderInlineMarkup(t("quote.text"))}"
          </blockquote>
          <div className="mt-10 flex items-center justify-center gap-4">
            <div className="w-12 h-12 rounded-full overflow-hidden bg-[var(--bg-2)]">
              <img src="/karim-portrait.webp" alt="Karim" className="w-full h-full object-cover object-top" onError={(e) => (e.currentTarget.style.display = "none")} />
            </div>
            <div className="text-left">
              <div className="text-[var(--text-0)] font-medium">Karim Benna</div>
              <div className="text-xs text-[var(--text-2)] font-mono">{t("quote.role")}</div>
            </div>
          </div>
        </div>
      </section>

      {/* BLOG TEASER */}
      {posts?.length > 0 && (
        <section className="border-t hairline bg-[var(--bg-1)]/30">
          <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-10 gap-6">
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[var(--gold)] font-medium mb-3">
                  · {t("blog.label")}
                </div>
                <h2 className="font-display text-3xl lg:text-4xl tracking-tight leading-tight">
                  {t("blog.titleA")} <span className="font-serif gold-grad">{t("blog.titleAccent")}</span>{t("blog.titleEnd")}
                </h2>
              </div>
              <Link to={pathWithLang("/blog")} data-testid="blog-cta" className="btn-ghost text-xs self-start lg:self-end">
                {t("common.allArticles")} <ArrowUpRight size={14} />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {posts.slice(0, 3).map((p) => {
                const tr = p.translations?.[lang];
                const title = (tr && tr.title) || tl(p.title) || p.title;
                const excerpt = (tr && tr.excerpt) || tl(p.excerpt) || p.excerpt;
                return (
                <Link
                  to={pathWithLang(`/blog/${p.slug}`)}
                  key={p.id}
                  data-testid={`blog-teaser-${p.slug}`}
                  className="svc-card flex flex-col group p-0 overflow-hidden"
                >
                  {p.cover_image && (
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={resolveMediaUrl(p.cover_image)}
                        alt={title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-wider text-[var(--text-2)] mb-3">
                      <Clock size={11} /> {p.reading_minutes} {t("blog.readingTime")}
                      <span className="text-[var(--text-3)]">·</span>
                      {new Date(p.created_at).toLocaleDateString(dateLocale, { day: "2-digit", month: "short", year: "numeric" })}
                    </div>
                    <h3 className="font-display text-lg text-[var(--text-0)] leading-snug group-hover:text-[var(--gold)] transition-colors mb-3">
                      {title}
                    </h3>
                    <p className="text-[var(--text-1)] text-sm leading-relaxed line-clamp-2 mb-4">
                      {excerpt}
                    </p>
                    <div className="mt-auto flex items-center gap-2 text-[var(--gold)] text-xs font-medium uppercase tracking-wider">
                      {t("common.readMore")} <ChevronRight size={12} />
                    </div>
                  </div>
                </Link>
              );
              })}
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20 lg:py-28">
        <div className="relative border hairline-gold rounded-md px-6 sm:px-12 lg:px-20 py-16 lg:py-24 overflow-hidden bg-gradient-to-br from-[var(--bg-1)] to-[var(--bg-0)]">
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[var(--gold)] opacity-[0.07] blur-[100px]" />
          <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-amber-500 opacity-[0.05] blur-[100px]" />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center relative">
            <div className="lg:col-span-8">
              <div className="pill mb-6">
                <span className="w-2 h-2 rounded-full bg-[var(--gold)] pulse-gold" />
                Limitless Solutions
              </div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                {t("cta.titleA")} <span className="font-serif gold-grad">{t("cta.titleAccent")}</span> {t("cta.titleEnd")}
              </h2>
              <p className="text-[var(--text-1)] mt-6 max-w-xl leading-relaxed text-lg">
                {t("cta.desc")}
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3 lg:items-end">
              <Link to={pathWithLang("/kontakt")} data-testid="cta-book-btn" className="btn-primary w-full lg:w-auto">
                <CalendarClock size={16} />
                {t("common.bookAppointment")}
              </Link>
              <Link to={pathWithLang("/kontakt")} data-testid="cta-contact-btn" className="btn-ghost text-xs w-full lg:w-auto justify-center">
                <MessageCircle size={14} /> {t("common.preferEmail")}
              </Link>
              <div className="text-xs text-[var(--text-2)] mt-3 lg:text-right">
                {t("cta.response")}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
